import Header from "@/components/header";
import HeaderSection from "@/modules/team/add/headerSection";
import MemberType from "@/modules/team/add/memberType";
import TeamMemberProfileInformation from "@/modules/team/add/profileInformation";
import TeamMemberService from "@/modules/team/add/teamService";
import useCreateMerchantUser from "@/services/merchantUser/hooks/useCreateMerchantUser";
import {
  CreateUserPayloadForm,
  merchantUserTypeMap,
} from "@/services/merchantUser/type";
import { Button, Card, Container, Group, Stack, Title } from "@mantine/core";
import { yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { UserFormProvider, useUserForm, useUserFormContext } from "./Context";
import moment from "moment-jalaali";
import {
  CategoryWithChecked,
  ServiceWithChecked,
} from "@/modules/team/add/teamService/type";
import { useListState } from "@mantine/hooks";
import merchantService from "@/services/merchant";
import useIsSmall from "@/hooks/useIsSmall";
import HelpButton from "@/modules/help/HelpButton";
import HelpPanel from "@/modules/help/HelpPanel";
import { teamMemberAddHelp } from "@/modules/help/topics/teamMemberAdd";
import AddMemberTour from "@/modules/team/add/AddMemberTour";

const schema = Yup.object().shape({
  FirstName: Yup.string().required("نام الزامی است"),
  LastName: Yup.string().required("نام خانوادگی الزامی است"),
  MobileNumber: Yup.string().required("شماره تلفن الزامی است"),
  genderForm: Yup.string().required("جنسیت الزامی است"),
  // markupPercentage: Yup.string().when("MarkupPercentage", (_, schema) => {
  //   return schema
  //     .test(
  //       "is-greater-than-zero",
  //       "مفدار باید بیشتر از 1 و کمتر از 100 باشد",
  //       (value) => Number(value) > 0 && Number(value) < 100
  //     )
  //     .required("مقدار را وارد نمایید")
  //     ;
  // })
  // password: Yup.string().required("رمز عبور الزامی است"),
  // merchantUserType: Yup.string().required("نوع کاربر الزامی است"),
});

function AddTeamMemberContent() {
  const [active, setActive] = useState<string>("1");
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const form = useUserFormContext();
  const navigate = useNavigate();
  const { mutate: addMerchantUser, isLoading: isAddingMerchantUser } =
    useCreateMerchantUser();
  const [selectedServices, handlers] = useListState<CategoryWithChecked>([]);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const isSmall = useIsSmall();
  const [helpOpened, setHelpOpened] = useState(false);
  const [tourRunning, setTourRunning] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current) {
        const rect = stickyRef.current.getBoundingClientRect();
        // Check if the element is stuck (top position is at or below 56px - header height)
        setIsSticky(rect.top <= 56);
      }
    };

    // Try to find the scrollable container
    const scrollableContainer = document.querySelector(
      "#dashboard-layout"
    ) as HTMLElement;

    if (scrollableContainer) {
      scrollableContainer.addEventListener("scroll", handleScroll);
      return () =>
        scrollableContainer.removeEventListener("scroll", handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const submit = async (values: CreateUserPayloadForm) => {

    const items =
      selectedServices?.flatMap(
        (service) =>
          (service.services as ServiceWithChecked[])
            ?.filter((x) => x.checked)
            .map((service) => service.id ?? 0) || []
      ) || [];
    
    setIsCheckingUser(true);
    try {
      const response = await merchantService.getMerchantForMerchantUser(
        values.MobileNumber ?? ""
      );

      if (response) {
        notifications.show({
          message: "شماره تلفن قبلا ثبت شده است",
          color: "yellow",
        });
        return;
      }
    } finally {
      setIsCheckingUser(false);
    }

    if (form.values.merchantUserTypeForm === undefined) {
      notifications.show({
        message: "نوع کاربر را انتخاب کنید",
        color: "yellow",
      });
      setActive("3");
      return;
    }
  
    // 0 is allowed; an empty field used to be sent as NaN and rejected by the server.
    const commission = form.values.markupPercentageFrom;
    if (commission === undefined || commission === null || commission === "" || !(Number(commission) >= 0 && Number(commission) <= 100)) {
      notifications.show({
        message: "درصد کمیسیون را بین ۰ تا ۱۰۰ وارد نمایید (۰ مجاز است)",
        color: "yellow",
      });
      setActive("3");
      return;
    }


    addMerchantUser(
      {
        FirstName: values.FirstName,
        LastName: values.LastName,
        MobileNumber: values.MobileNumber,
        ServiceIds: items,
        Gender: Number(values?.genderForm),
        BirthDate: (() => {
          const y = values?.year?.toString() || "";
          const m = values?.month?.toString() || "";
          const d = values?.day?.toString() || "";
          if (!y || !m || !d) return "";
          const jalali = `${y}/${m}/${d}`;
          const parsed = moment(jalali, "jYYYY/jMM/jDD", true);
          return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        })(),
        CalendarColor: values?.CalendarColor,
        MarkupPercentage: Number(form.values.markupPercentageFrom),
        MerchantUserType:
          merchantUserTypeMap[
          form.values.merchantUserTypeForm as keyof typeof merchantUserTypeMap
          ],
        file: values?.profileImage![0]?.file || undefined,
        "UserInfo.UserId": undefined,
        "UserInfo.MobileNumber": "",
        JobTitle: values?.JobTitle || "",
        ShowOnWebsite: values?.ShowOnWebsite ?? true,
      },
      {
        onSuccess() {
          notifications.show({
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          navigate("/dashboard/teams/teamMembers");
        },
      }
    );
  };

  useEffect(() => {
    if (Object.keys(form.errors).length > 0) {
      setActive("1");
    }
  }, [form.errors]);

  return (
    <>
      <Header
        title="تیم"
        showBack
        route="/dashboard/teams/teamMembers"
        customComponent={
          <HelpButton
            active={helpOpened}
            onClick={() => {
              setTourRunning(false);
              setHelpOpened((opened) => !opened);
            }}
          />
        }
      />
      <AddMemberTour run={tourRunning} setRun={setTourRunning} />
      <Group align="flex-start" justify="center" wrap="nowrap" gap="lg" px={isSmall ? 0 : "md"}>
      <Stack gap={0} w="100%" maw={960} style={{ minWidth: 0 }}>
      <Container
        w="100%"
        size="md"
        bg="white"
        mb={isSmall ? "100px" : "50px"}
        px="0"
      >
        <Title order={4} pb="0" px="md">
          افزودن به تیم
        </Title>
        <Stack justify="start" flex={1}>
          <Stack
            ref={stickyRef}
            gap="lg"
            py="sm"
            styles={{
              root: {
                position: "sticky",
                top: "56px", // Header height to stick below the header
                zIndex: 15, // Higher than header but still allows proper stacking
                backgroundColor: "white",
                borderBottom: isSticky
                  ? "1px solid var(--mantine-color-gray-2)"
                  : "none",
                transition: "border-bottom 0.2s ease",
              },
            }}
          >
            <HeaderSection active={active} setActive={setActive} />
          </Stack>
          <form
            onSubmit={form.onSubmit(submit)}
            name="add-customer"
            className="w-100"
            id="add-customer"
          >
            <Stack px="md">
              {active === "1" && <TeamMemberProfileInformation />}
              {active === "2" && (
                <TeamMemberService
                  handlers={handlers}
                  values={selectedServices}
                />
              )}
              {active === "3" && <MemberType />}
            </Stack>
          </form>
        </Stack>
      </Container>
      {isSmall ? (
        <Card
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
            },
          }}
          withBorder
          radius={0}
        >
          <Button
            type="submit"
            size="lg"
            fullWidth

            form="add-customer"
            loading={isCheckingUser || isAddingMerchantUser}
            id="team-add-submit"
          >
            افزودن
          </Button>
        </Card>
      ) : (
        <Container w="100%" size="md" bg="white" mb="50px" px="0">
          <Button
            type="submit"
            size="lg"
            fullWidth
            form="add-customer"
            loading={isCheckingUser || isAddingMerchantUser}
            id="team-add-submit"
          >
            افزودن
          </Button>
        </Container>
      )}
      </Stack>
      <HelpPanel
        topic={teamMemberAddHelp}
        opened={helpOpened}
        onClose={() => setHelpOpened(false)}
        activeStep={active}
        onStartTour={() => {
          setHelpOpened(false);
          setTourRunning(true);
        }}
      />
      </Group>
    </>
  );
}

function AddTeamMember() {
  const form = useUserForm({
    mode: "uncontrolled",
    validate: yupResolver(schema),
    initialValues: {
      FirstName: "",
      LastName: "",
      MobileNumber: "",
      MerchantUserType: undefined,
      file: undefined,
      services: [],
      day: "",
      month: "",
      year: "",
      profileImage: [],
      BirthDate: undefined,
      CalendarColor: "",
      Gender: undefined,
      ServiceIds: [],
      // The switch shows this, and the submit already defaulted to true.
      ShowOnWebsite: true,
    },
  });

  console.log(form?.values);

  return (
    <UserFormProvider form={form}>
      <AddTeamMemberContent />
    </UserFormProvider>
  );
}

export default AddTeamMember;
